import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { invalidateEntities, listEntities } from '../../../services';

/**
 * Data-model entities for the Data step (CT-5191) and the entity picker modal
 * (CT-5192).
 *
 * Which entities a surface *shows* is its own decision — `featured` is a flag
 * on the row, not a filter here; the service derives it from how recently each
 * entity was updated, because the data-model catalog has no such field.
 *
 * `search` is sent **in the request**, not applied to the response.
 *
 * @param {{ search?: string }} [options]
 */
export const useEntities = ({ search } = {}) => {
  const [state, setState] = useState({ entities: [], error: null });
  const [resolvedKey, setResolvedKey] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchIdRef = useRef(0);

  const requestKey = `${search ?? ''}|${reloadToken}`;
  const isLoading = resolvedKey !== requestKey;

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    listEntities({ search })
      .then(({ entities }) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ entities, error: null });
      })
      .catch((error) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ entities: [], error });
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolvedKey(requestKey);
      });

    return () => {
      mounted = false;
    };
  }, [requestKey, search]);

  /*
    Forget the cached answer first, then ask again.

    `listEntities` is cached in the service layer so the Data step's two
    consumers — this hook and `useEntityPicker` — share one request instead of
    each starting their own on every remount (CT-5579). That is also why this
    has to invalidate: without it, the only thing "try again" would retry is
    reading the same cache entry.
  */
  const refetch = useCallback(() => {
    invalidateEntities();
    setReloadToken((n) => n + 1);
  }, []);

  return useMemo(
    () => ({ entities: state.entities, isLoading, error: state.error, refetch }),
    [isLoading, refetch, state.entities, state.error],
  );
};

export default useEntities;
